/* Copyright (c) 2020 Paul Nebel */

import React from 'react';
import PropTypes from 'prop-types';
import AnimateOnChange from 'react-animate-on-change';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import ChordSymbol from './chord-symbol';
import * as styles from '../styles/components.module.css';
import * as animationStyles from '../styles/animations.module.css';
import { parseChord, Mode, getRootNote } from '../core/utils/chord-parser';

const getMode = (m) => {
    let currentMode = m;
    if (m === Mode.MIXED) {
        currentMode = Math.round(Math.random());
    }
    return currentMode;
};

class Thumbnail extends React.Component {
    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            index,
            style,
            chord,
            notifySelectedChordChanged,
            metronomeState,
            playHeadIndex,
            bpm,
            mode,
        } = nextProps;

        const newState = {
            ...prevState,
            style,
            chord,
            bpm,
            mode,
        };

        if (playHeadIndex === index && !prevState.selected) {
            newState.selected = true;
        }
        if (prevState.selected && playHeadIndex !== index) {
            newState.selected = false;
        }
        if (prevState.chord !== chord) {
            newState.chordSymbol = parseChord(chord, getMode(prevState.mode));
        }

        if (prevState.mode !== mode) {
            newState.chordSymbol = parseChord(chord, getMode(mode));
        }
        if (newState.selected) {
            notifySelectedChordChanged(
                newState.chord,
                newState.chordSymbol
            );
        }

        if (newState.selected) {
            notifySelectedChordChanged(newState.chord, newState.chordSymbol);
            switch (metronomeState) {
                case 'intro':
                case 'pausedActive':
                    newState.baseClassName = 'thumbnailSelectedActive';
                    newState.backgroundColor = '#F0AD4E';
                    break;
                case 'active':
                    newState.baseClassName = 'thumbnailSelectedActive';
                    newState.backgroundColor = '#dee5ed';
                    break;
                default:
                    newState.baseClassName = 'thumbnailSelectedInactive';
                    newState.backgroundColor = '#D9534F';
            }
            newState.color = '#0A0A0A';
        } else {
            newState.baseClassName = 'thumbnailUnselected';
            newState.backgroundColor = '#2B3E50';
            newState.color = '#dee5ed';
        }

        // handle initial rendering
        if (playHeadIndex == undefined && prevState.selected) {
            newState.baseClassName = 'thumbnailSelectedInactive';
            newState.backgroundColor = '#D9534F';
            newState.color = '#0A0A0A';
            newState.selected = true;
        }

        if (!isEqual(prevState, newState)) {
            return newState;
        } else {
            return null;
        }
    }

    constructor(props, context) {
        super(props, context);
        const { index, style, mode, chord } = props;
        this.state = {
            selected: index === 0,
            style,
            backgroundColor: '#ccc',
            color: 'white',
            baseClassName: 'thumbnailSelectedInactive',
            bpm: 60,
            mode,
            chord,
            chordSymbol: parseChord(chord, getMode(mode)),
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        const selectedChanged = this.state.selected !== nextState.selected;
        const modeChanged = this.state.mode !== nextState.mode;
        return (
            selectedChanged ||
            (this.state.selected &&
                this.props.metronomeState !== nextProps.metronomeState) ||
            nextState.selected ||
            modeChanged
        );
    }

    render() {
        const style = this.state.style;
        const selected = this.state.selected;
        return (
            <AnimateOnChange
                customTag="div"
                baseClassName={styles[this.state.baseClassName]}
                animationClassName={animationStyles['thumbnail_fade_active']}
                animate={selected && this.props.metronomeState === 'intro'}
                style={{ animationDuration: `${(60 / this.state.bpm) * 4}s` }}
            >
                <ChordSymbol
                    chordSymbol={getRootNote(this.state.chordSymbol)}
                    x={style.x}
                    y={style.y}
                    width={style.width}
                    height={style.height}
                    color={this.state.color}
                    backgroundColor="transparent"
                    font={style.font}
                    dynamic={false}
                />
            </AnimateOnChange>
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        notifySelectedChordChanged: (chord, chordSymbol) =>
            dispatch({
                type: `SELECTED_CHORD_CHANGED`,
                chord,
                chordSymbol,
            }),
    };
};

const mapStateToProps = ({
    metronomeState,
    playHeadIndex,
    bpm,
    mode,
}) => {
    return {
        metronomeState,
        playHeadIndex,
        bpm,
        mode,
    };
};

const style = {
    x: 100,
    y: 0,
    width: 50,
    height: 30,
    backgroundColor: 'pink',
    color: 'black',
    font: '15px sans-serif',
};

Thumbnail.propTypes = {
    index: PropTypes.number,
    chord: PropTypes.string,
    style: PropTypes.object,
    notifySelectedChordChanged: PropTypes.func.isRequired,
    metronomeState: PropTypes.string,
    bpm: PropTypes.number,
    playHeadIndex: PropTypes.number,
    mode: PropTypes.number,
};

const chord = 'C';
Thumbnail.defaultProps = {
    index: 0,
    chord,
    style,
    metronomeState: 'inactive',
    mode: Mode.MAJOR,
};

export default connect(mapStateToProps, mapDispatchToProps)(Thumbnail);
